import React from 'react';
import PropTypes from 'prop-types';
import {get} from 'lodash';
import className from 'classnames';
import _moment from 'moment-timezone';
import {timeDays} from 'd3-time';

const DateSelector = ({
  selectedDateTime,
  hideTime = false,
  enablePast = false,
  inputName,
  onChange = () => {},
  useUtc = false,
}) => {
  const moment = (x) => {
    return _moment.tz(x, useUtc ? 'UTC' : 'Asia/Tokyo')
  }

  const exportTimeStamp = (dateTime) => {
    return dateTime.length > 0 ? Number(moment(dateTime)) : ''
  }

  const FORMAT = hideTime ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'
  const [selectedDateTimeState, selectedDateTimeStateSet] = React.useState(get(selectedDateTime, 'length', 0) > 0 ? moment(selectedDateTime).format(FORMAT) : '')
  const [$$panelDateTime, $$panelDateTimeSet] = React.useState(get(selectedDateTime, 'length', 0) > 0 ? moment(selectedDateTime) : moment())
  const selectedDateTimeStatePrev = React.useRef(selectedDateTimeState)
  const [isActive, isActiveSet] = React.useState(false)

  React.useEffect(() => {
    if (selectedDateTimeState != selectedDateTimeStatePrev.current) {
      onChange(exportTimeStamp(selectedDateTimeState))
      selectedDateTimeStatePrev.current = selectedDateTimeState
    }
  }, [selectedDateTimeState])

  return (
    <div className='DateSelector'>
      <input
        name={inputName}
        value={exportTimeStamp(selectedDateTimeState)}
        data-datetime={_moment(exportTimeStamp(selectedDateTimeState)).format()}
        data-datetime-utc={_moment.utc(exportTimeStamp(selectedDateTimeState)).format()}
        type='hidden'
      />

      <a
        className={className('DateSelector__overlay', {
          'DateSelector__overlay--active': isActive,
        })}
        onClick={() => {
          isActiveSet(false)
        }}
      />

      <div className='DateSelector__info'>
        <a
          className={className('DateSelector__current', {
          })}
          onClick={() => {
            if (!enablePast) {
              if (moment(selectedDateTimeState).isBefore(moment())) {
                selectedDateTimeStateSet(moment().format(FORMAT))
              }
            }

            isActiveSet(true)
          }}
        >
          {(() => {
            return (
              <input
                className={className('DateSelector__current__date', {
                })}
                value={selectedDateTimeState}
                placeholder={FORMAT}
                pattern={hideTime ? '[0-9]{4}-[0-9]{2}-[0-9]{2}' : '[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}'}
                onChange={(e) => {
                  const value = get(e, 'target.value')

                  selectedDateTimeStateSet(value)
                  $$panelDateTimeSet((value.length > 0 ? moment(value) : moment()).startOf('month'))
                }}
              />
            )
          })()}
        </a>
      </div>

      <div className={className('DateSelector__panel', {
        'DateSelector__panel--active': isActive,
      })}>
        {(
          selectedDateTimeState.length == 0 ||
          moment(selectedDateTimeState).isValid()
        ) ?
          <React.Fragment>
            <div className='DateSelector__calendar__nav'>
              <a
                className={className('DateSelector__calendar__nav__link', {
                  'DateSelector__calendar__nav__link--disabled': !enablePast && $$panelDateTime.month() == moment().month(),
                })}
                onClick={() => {
                  $$panelDateTimeSet(moment($$panelDateTime).subtract(1, 'month'))
                }}
              />

              <p className='DateSelector__calendar__title'>{$$panelDateTime.format('MMM YYYY')}</p>

              <a
                className={className('DateSelector__calendar__nav__link', {
                  'DateSelector__calendar__nav__link--right': true,
                })}
                onClick={() => {
                  $$panelDateTimeSet(moment($$panelDateTime).add(1, 'month'))
                }}
              />
            </div>

            {(() => {
              const $$firstDayOfMonth = moment($$panelDateTime).startOf('month').startOf('week');
              const $$lastDayOfMonth = moment($$panelDateTime).endOf('month').endOf('week');
              const dayInterval = timeDays($$firstDayOfMonth, $$lastDayOfMonth);

              return (
                <ul className='DateSelector__calendar'>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((x, i) => (
                    <li className='DateSelector__calendar__date DateSelector__calendar__date--header' key={i}>{x}</li>
                  ))}
                  {dayInterval.map((x, i) => {
                    const $$date = moment(x).endOf('day')

                    return (
                      <li
                        key={i}
                        className={className('DateSelector__calendar__date', {
                          'DateSelector__calendar__date--selected-date': $$date.isSame(moment(selectedDateTimeState), 'd'),
                          'DateSelector__calendar__date--diff-month': !$$date.isSame($$panelDateTime, 'month'),
                          'DateSelector__calendar__date--past': !enablePast && $$date.isBefore(moment(), 'day'),
                        })}
                      >
                        <a
                          onClick={() => {
                            const time = selectedDateTimeState.length > 0 ? moment(selectedDateTimeState).format('HH:mm') : '00:00'
                            let $$_dateTime = moment(`${$$date.format('YYYY-MM-DD')} ${time}`)

                            if (!enablePast) {
                              if ($$_dateTime.isBefore(moment())) {
                                $$_dateTime = moment()
                              }
                            }

                            selectedDateTimeStateSet($$_dateTime.format(FORMAT))
                            $$panelDateTimeSet($$date.startOf('month'))
                          }}
                        >
                          {$$date.format('D')}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              )
            })()}

            {(
              !hideTime &&
              moment(selectedDateTimeState).isValid() > 0
            ) &&
              <React.Fragment>
                {(() => {
                  return (
                    <div className='DateSelector__time'>
                      <select
                        className='Select'
                        value={moment(selectedDateTimeState).format('H')}
                        onChange={(e) => {
                          selectedDateTimeStateSet(moment(selectedDateTimeState).hour(get(e, 'target.value')).format(FORMAT))
                        }}
                      >
                        {(() => {
                          let hours = Array.from(Array(24)).map((x, i) => i)

                          if (!enablePast) {
                            hours = hours.filter((x, i) => {
                              return moment(selectedDateTimeState).hour(x).isSameOrAfter(moment().startOf('hour'))
                            })
                          }

                          return (
                            <React.Fragment>
                              {hours.map((x, i) => (
                                <option
                                  key={i}
                                  value={x}
                                >
                                  {x}
                                </option>
                              ))}
                            </React.Fragment>
                          )
                        })()}
                      </select>

                      <select
                        className='Select'
                        value={moment(selectedDateTimeState).format('m')}
                        onChange={(e) => {
                          selectedDateTimeStateSet(moment(selectedDateTimeState).minute(get(e, 'target.value')).format(FORMAT))
                        }}
                      >
                        {(() => {
                          let minutes = Array.from(Array(60)).map((x, i) => i)

                          if (!enablePast) {
                            minutes = minutes.filter((x, i) => {
                              return moment(selectedDateTimeState).minutes(x).isSameOrAfter(moment().startOf('minute'))
                            })
                          }

                          return (
                            <React.Fragment>
                              {minutes.map((x, i) => (
                                <option
                                  key={i}
                                  value={x}
                                >
                                  {x}
                                </option>
                              ))}
                            </React.Fragment>
                          )
                        })()}
                      </select>
                    </div>
                  )
                })()}
              </React.Fragment>
            }
          </React.Fragment>
        :
          <div className='DateSelector__panel__p'>
            Date is invalid
          </div>
        }
      </div>
    </div>
  );
}

DateSelector.propTypes = {
};

export default DateSelector;
